import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { GRID_CELL_DATA_QA, RESULTS_PER_PAGE } from 'search/enums/constants';
import {
  getAppliedFilters,
  getCurrentSort,
  getImageType,
} from 'shared/utils/gridEventTrackingUtils';
import { getGridReleaseDateDisplay } from 'shared/utils/getGridReleaseDateDisplay';
import { getLowestPriceCents } from 'productTemplate/utils/getLowestPriceCents';
import { getPageNumber } from 'search/utils/getPageNumber';
import { getSearchFiltersForPdp } from 'productTemplate/utils/getSearchFiltersForPdp';
import { getSellerImage } from 'shared/utils/getSellerImage';
import { GridCellLink } from 'shared/components/Grid/GridCellLink';
import { GridCellProductImage } from 'shared/components/Grid/GridCellProductImage';
import { GridCellProductInfo } from 'shared/components/Grid/GridCellProductInfo';
import { GridCellWrapper } from 'shared/components/Grid/styles/GridStyles';
import { IConstructorResult } from 'shared/types/ConstructorResult';
import { isTouchDevice } from 'shared/utils/isTouchDevice';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { setSearchFiltersForPdp } from 'store/slice/searchSlice';
import { useConstructorClient } from 'shared/hooks/useConstructorClient';
import { useCurrency } from 'shared/hooks/useCurrency';
import { useDebouncedValue } from 'shared/hooks/useDebouncedValue';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';

const GridCellSizePrice = dynamic(() => import('shared/components/Grid/GridCellSizePrice'), {
  ssr: false,
});

interface IGridCell {
  browseKey?: string;
  browseValue?: string;
  currencyRates: { [key: string]: number };
  disableLazyLoad?: boolean;
  globalPricingId: string;
  position: number;
  product: IConstructorResult;
  resultsCount?: number;
}

export const GridCell = ({
  browseKey,
  browseValue,
  currencyRates,
  disableLazyLoad = false,
  globalPricingId,
  product,
  position,
  resultsCount,
}: IGridCell) => {
  const [isActiveCell, setIsActiveCell] = useState(false);
  const debouncedIsActiveCell = useDebouncedValue(isActiveCell, 100);
  const { asPath, query, pathname } = useRouter();
  const { query: searchQuery, sortBy, sortOrder, recently_released: recentlyReleased } = query;
  const { user } = useStoreSelector((state) => state.auth);
  const storeDispatch = useStoreDispatch();
  const ConstructorClient = useConstructorClient();
  const { selectedCurrencyCode } = useCurrency();
  const { price, retailPrice } = getLowestPriceCents({
    selectedCurrencyCode,
    result: product,
    currencyRates,
    globalPricingId,
    query,
  });
  const { data, value: name, result_id: resultId } = product;
  const {
    category,
    cheapest_product: cheapestProduct,
    id,
    image_url: imageUrl,
    product_condition: productCondition,
    product_type: productType,
    release_date: releaseDate,
    season,
    slug,
  } = data;

  const releaseDateDisplay = getGridReleaseDateDisplay(releaseDate, season, {
    query,
    asPath,
    pathname,
  });
  const sellerImage = getSellerImage(query, cheapestProduct);
  const imageType = getImageType(imageUrl, sellerImage);
  const eventTrackingEventContext = useEventContext();

  const handleMouseEnter = () => {
    if (isTouchDevice()) return;
    setIsActiveCell(true);
  };

  const handleMouseLeave = () => {
    if (isTouchDevice()) return;
    setIsActiveCell(false);
  };

  const handleOnClick = () => {
    // Store the search filters to apply to PDP's Buy Button
    storeDispatch(setSearchFiltersForPdp(getSearchFiltersForPdp(query)));

    sendTrackingEvent('CONSTRUCTOR_GRID_CELL_TAP', {
      currentSort: getCurrentSort(sortBy, sortOrder, recentlyReleased),
      event_context: eventTrackingEventContext,
      is_filtered: getAppliedFilters(query) !== '',
      is_preview: false,
      objectID: id,
      pdp_image_type: imageType,
      position: position?.toString(),
      product_template_slug: slug,
      result_id: resultId,
      ...(getAppliedFilters(query) && { applied_filters: getAppliedFilters(query) }),
      ...(searchQuery && { search_string: searchQuery.toString() }),
      ...(user && { userID: user.id }),
    });

    if (searchQuery) {
      ConstructorClient?.tracker?.trackSearchResultClick(searchQuery, {
        item_name: name,
        item_id: id,
        result_id: resultId,
      });
    } else {
      ConstructorClient?.tracker?.trackBrowseResultClick({
        filter_name: browseKey,
        filter_value: browseValue,
        item_id: id,
        result_id: resultId,
        result_count: resultsCount,
        result_page: getPageNumber(position),
        result_position_on_page: position,
        num_results_per_page: RESULTS_PER_PAGE,
        ...(getAppliedFilters(query) && { selected_filters: JSON.parse(getAppliedFilters(query)) }),
      });
    }
  };

  return (
    <GridCellWrapper
      data-qa={GRID_CELL_DATA_QA}
      data-grid-cell-position={position}
      data-grid-cell-name={id}
      onMouseOver={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <GridCellLink
        aria-label={normalizeValue(slug)}
        onClick={handleOnClick}
        to={`/${category === 'shoes' ? 'sneakers' : 'apparel'}/${slug}`}
      >
        <GridCellProductInfo
          id={id}
          isActiveCell={isActiveCell}
          name={name}
          price={price}
          productCondition={productCondition}
          releaseDate={releaseDateDisplay}
          retailPrice={retailPrice}
          slug={slug}
        />
        {debouncedIsActiveCell && (
          <GridCellSizePrice productTemplateId={id} retailPrice={retailPrice} />
        )}
        <GridCellProductImage
          disableLazyLoad={disableLazyLoad}
          imageSrc={sellerImage || imageUrl}
          isActiveCell={isActiveCell}
          isSellerImage={!!sellerImage}
          name={name}
          productType={productType}
        />
      </GridCellLink>
    </GridCellWrapper>
  );
};
